"use client";
import Link from "next/link";
import { Button } from "../ui/button";
import { Reveal } from "../reveal";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { HeroSectionType } from "../features/hero";
import { PortableText, PortableTextReactComponents } from "next-sanity";
import { getURL } from "@/lib/utils";
import { iconsMap } from "@/components/icons";

const H1 = ({ children }: { children?: React.ReactNode }) => (
  <h1 className="text-start md:text-center font-semibold text-3xl sm:text-4xl lg:text-6xl w-full max-w-screen-sm mx-auto leading-tight">
    {children}
  </h1>
);

const H2 = ({ children }: { children?: React.ReactNode }) => (
  <h2 className="text-start md:text-center font-medium text-2xl lg:text-3xl max-w-screen-sm mx-auto text-primary-text-100 leading-snug">
    {children}
  </h2>
);

const H3 = ({ children }: { children?: React.ReactNode }) => (
  <h3 className="text-start md:text-center font-medium text-xl lg:text-2xl max-w-screen-sm mx-auto text-primary-text-200">
    {children}
  </h3>
);

const H4 = ({ children }: { children?: React.ReactNode }) => (
  <h4 className="text-start md:text-center font-semibold text-lg lg:text-xl max-w-screen-sm mx-auto text-primary-text-300">
    {children}
  </h4>
);

const H5 = ({ children }: { children?: React.ReactNode }) => (
  <h5 className="text-start md:text-center font-semibold text-base lg:text-lg max-w-screen-sm mx-auto text-primary-text-400 uppercase tracking-wide">
    {children}
  </h5>
);

const H6 = ({ children }: { children?: React.ReactNode }) => (
  <h6 className="text-start md:text-center font-medium text-sm lg:text-base max-w-screen-sm mx-auto text-primary-text-500 tracking-wide">
    {children}
  </h6>
);

export const components: Partial<PortableTextReactComponents> = {
  block: {
    h1: H1,
    h2: H2,
    h3: H3,
    h4: H4,
    h5: H5,
    h6: H6,
    normal: ({ children }) => (
      <p className="text-start md:text-center max-w-screen-sm mx-auto font-medium text-lg text-primary-text-200">
        {children}
      </p>
    ),
  },
};

function hasPortableTextContent(value: unknown) {
  if (!Array.isArray(value)) return Boolean(value);

  return value.some((block) => {
    if (!block || typeof block !== "object") return false;

    const children = (block as { children?: unknown }).children;
    if (!Array.isArray(children)) return true;

    return children.some((child) => {
      if (!child || typeof child !== "object") return false;

      const text = (child as { text?: unknown }).text;
      return typeof text === "string" && text.trim().length > 0;
    });
  });
}

export function Hero({ data }: { data: HeroSectionType }) {
  const { title, subtitle, description, ctas } = data;
  const isMobile = useMediaQuery("(max-width: 768px)");
  const hasDescription = hasPortableTextContent(description);

  return (
    <section className="-top-[84px] -mb-[84px] bg-primary-900  w-full overflow-hidden relative before:content-[''] before:absolute before:left-0 before:-top-20 before:w-1/2 before:h-full before:bg-[url('/index/hero/right.svg')] before:bg-no-repeat before:bg-cover before:transform before:-scale-x-100 after:content-[''] after:absolute after:right-0 after:-top-20 after:w-1/2 after:h-full after:bg-[url('/index/hero/right.svg')] after:bg-no-repeat after:bg-cover">
      <div className="max-w-screen-2xl mx-auto overflow-hidden z-[2] relative before:absolute before:content-[''] before:w-full before:h-full before:bg-[url('/index/hero/center.svg')] before:bg-contain before:bg-center before:bg-no-repeat py-[130px] sm:py-[140px] lg:py-[180px] before:top-0">
        <Reveal>
          <div className="relative flex flex-col items-center justify-center text-primary-text-50 max-w-screen-lg w-full mx-auto gap-0 px-6">
            <div className="flex w-full gap-6 flex-col items-start justify-start">
              <PortableText value={title} components={components} />
              {subtitle && <PortableText value={subtitle} components={components} />}
              {description && hasDescription && (
                <PortableText value={description} components={components} />
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-center gap-3 sm:gap-4 w-full flex-wrap mt-6">
              {ctas?.map((cta, i) => {
                const href = getURL(cta);
                return (
                  <Button
                    key={i}
                    variant={cta.variant || "default"}
                    radius="full"
                    className="w-full sm:max-w-xs sm:flex-1"
                    size={isMobile ? "default" : "xl"}
                    asChild
                  >
                    <Link
                      href={href}
                      target={cta.openInNewTab ? "_blank" : "_self"}
                      rel={cta.openInNewTab ? "noopener noreferrer" : undefined}
                    >
                      {cta.textBeforeIcon}
                      {cta.icon && iconsMap[cta.icon]}
                      {cta.textAfterIcon}
                    </Link>
                  </Button>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
